import { ReactNode, useCallback, useMemo, useState } from 'react';

import { cx } from '@libs/classnames';
import { formatName } from '@libs/formatNames';
import { CollapseIcon } from '@ui/components/CollapseIcon';
import { HorizontalScroll } from '@ui/components/HorizontalScroll';
import { Table, TableCell, TableRow } from '@ui/components/Table';

import { ClockControlType, ParticipantDto } from '@features/shared/api/typings';
import { TIME_CONTROL } from '@features/shared/constants';

import styles from './CardParticipantList.module.css';

type Team = {
  id: string;
  name: string;
};

export type Props = {
  className?: string;
  hideNumbers?: boolean;
  teams?: Team[];
  participants?: ParticipantDto[];
  clockControl: ClockControlType;
  highlightedUserId?: string;
  groupTitle?: string;
  showEmails?: boolean;
  showNumberInTeam?: boolean;
  isRcf: boolean;
  renderShieldQuickPeek?: (userId: string) => ReactNode;
};

export function CardParticipantList({
  className,
  hideNumbers,
  teams,
  participants,
  clockControl,
  highlightedUserId,
  groupTitle,
  showEmails,
  showNumberInTeam,
  isRcf,
  renderShieldQuickPeek,
}: Props) {
  const [visibleGroups, setVisibleGroups] = useState<Set<string>>(new Set());
  const onToggle = useCallback(
    (teamId: string) => {
      if (visibleGroups.has(teamId)) {
        visibleGroups.delete(teamId);
      } else {
        visibleGroups.add(teamId);
      }
      setVisibleGroups(new Set(visibleGroups));
    },
    [visibleGroups, setVisibleGroups],
  );

  const { hasGroups, participantsByGroupId } = useMemo(() => {
    const participantsByGroupId: Record<string, ParticipantDto[]> = {};
    let hasGroups = false;

    participants?.forEach((p) => {
      const groupId = p.teamId || 'free';
      if (!participantsByGroupId[groupId]) participantsByGroupId[groupId] = [];
      participantsByGroupId[groupId]!.push(p);
      if (p.groupName) hasGroups = true;
    });

    return { hasGroups, participantsByGroupId };
  }, [participants]);

  const showTeams = Boolean(teams && teams.length > 0);
  const groupIsRegion = groupTitle === 'Регион';
  const showGroups = hasGroups && !isRcf;
  const isManager = Boolean(showEmails);

  return (
    <HorizontalScroll>
      <Table className={cx(className, styles.participantsList)}>
        <TableRow header>
          {!hideNumbers && <TableCell />}
          <TableCell
            className={styles.nameColumn}
            colSpan={1 + (showTeams && !hideNumbers ? 1 : 0)}
          >
            Имя
          </TableCell>
          {showEmails && <TableCell leftGap>Email</TableCell>}
          {showGroups && <TableCell leftGap>{groupTitle}</TableCell>}
          {isRcf && <TableCell leftGap>Регион</TableCell>}
          {isRcf && isManager && <TableCell leftGap>Год рожд.</TableCell>}
          {isRcf && isManager && <TableCell right>ФШР&nbsp;ID</TableCell>}
          <TableCell colSpan={2} centered>
            Рейтинг {isRcf ? 'ФШР' : null}
          </TableCell>
        </TableRow>

        {participantsByGroupId['free'] &&
          participantsByGroupId['free'].length > 0 &&
          participantsByGroupId['free'].map((p, i) => (
            <ParticipantListItem
              key={p.userId}
              hideNumbers={hideNumbers}
              no={++i}
              participant={p}
              clockControl={clockControl}
              highlighted={p.userId === highlightedUserId}
              showEmails={showEmails}
              showGroups={showGroups}
              showTeams={showTeams}
              isInTeam={false}
              isRcf={isRcf}
              groupIsRegion={groupIsRegion}
              renderShieldQuickPeek={renderShieldQuickPeek}
            />
          ))}

        {showTeams &&
          teams?.map((g, i) => (
            <GroupListItem
              key={g.id}
              hideNumbers={hideNumbers}
              no={i + 1}
              team={g}
              clockControl={clockControl}
              participants={participantsByGroupId[g.id] || []}
              highlightedUserId={highlightedUserId}
              showEmails={showEmails}
              showNumberInTeam={showNumberInTeam}
              showGroups={showGroups}
              renderShieldQuickPeek={renderShieldQuickPeek}
              isToggled={visibleGroups.has(g.id)}
              onToggle={onToggle}
              isRcf={isRcf}
              groupIsRegion={groupIsRegion}
            />
          ))}
      </Table>
      {(!participants || !participants.length) && (
        <div className={styles.placeholder}>Нет участников</div>
      )}
    </HorizontalScroll>
  );
}

type GroupListItemProps = {
  no: number;
  hideNumbers?: boolean;
  team: Team;
  clockControl: ClockControlType;
  participants: ParticipantDto[];
  highlightedUserId?: string;
  showEmails?: boolean;
  showNumberInTeam?: boolean;
  showGroups: boolean;
  isToggled: boolean;
  onToggle: (teamId: string) => void;
  isRcf: boolean;
  groupIsRegion: boolean;
  renderShieldQuickPeek?: (userId: string) => ReactNode;
};

function GroupListItem({
  no,
  hideNumbers,
  team,
  clockControl,
  participants,
  highlightedUserId,
  showEmails,
  showNumberInTeam,
  showGroups,
  isToggled,
  onToggle,
  isRcf,
  groupIsRegion,
  renderShieldQuickPeek,
}: GroupListItemProps) {
  const handleNameClick = useCallback(() => {
    onToggle(team.id);
  }, [onToggle, team.id]);

  let nameColSpan = 3;
  if (!hideNumbers) nameColSpan += 1;
  if (showEmails) nameColSpan += 1;
  if (showGroups) nameColSpan += 1;
  if (isRcf) nameColSpan += showEmails ? 3 : 1;

  return (
    <>
      <TableRow group hoverable>
        {hideNumbers ? null : <TableCell className={styles.numberColumn}>{no}</TableCell>}
        <TableCell colSpan={nameColSpan} onClick={handleNameClick}>
          <div className={styles.titleContainer}>
            <div className={styles.teamName}>{team.name}</div>
            <CollapseIcon toggled={isToggled} />
          </div>
        </TableCell>
      </TableRow>

      {participants.length > 0 &&
        isToggled &&
        participants.map((p, i) => (
          <ParticipantListItem
            key={p.userId}
            hideNumbers={hideNumbers}
            no={showNumberInTeam ? (p.numberInTeam ?? i + 1) : i + 1}
            participant={p}
            clockControl={clockControl}
            highlighted={p.userId === highlightedUserId}
            showEmails={showEmails}
            showGroups={showGroups}
            showTeams
            isInTeam
            isRcf={isRcf}
            groupIsRegion={groupIsRegion}
            renderShieldQuickPeek={renderShieldQuickPeek}
          />
        ))}
    </>
  );
}

type ParticipantListItemProps = {
  no?: number;
  hideNumbers?: boolean;
  participant: ParticipantDto;
  clockControl: ClockControlType;
  highlighted?: boolean;
  showEmails?: boolean;
  showGroups: boolean;
  showTeams: boolean;
  isInTeam?: boolean;
  isRcf: boolean;
  groupIsRegion: boolean;
  renderShieldQuickPeek?: (userId: string) => ReactNode;
};
function ParticipantListItem({
  no,
  hideNumbers,
  participant,
  clockControl,
  highlighted,
  showEmails,
  showGroups,
  showTeams,
  isInTeam,
  isRcf,
  groupIsRegion,
  renderShieldQuickPeek,
}: ParticipantListItemProps) {
  const time = (
    <>
      <TableCell slim leftGap right top>
        {TIME_CONTROL[clockControl]} ∙
      </TableCell>
      <TableCell slim right top>
        {participant.effectiveInitialRating}
      </TableCell>
    </>
  );

  const groupName = participant.groupName || '';

  const yearOfBirth =
    participant.rcfYearOfBirth ??
    (typeof participant.birthdayTimestampMs === 'number'
      ? new Date(participant.birthdayTimestampMs).getUTCFullYear()
      : null);

  return (
    <TableRow hoverable active={highlighted}>
      {isInTeam && !hideNumbers && <TableCell className={styles.numberColumn} />}
      {!hideNumbers && (
        <TableCell className={styles.numberColumn} top>
          {no}
        </TableCell>
      )}
      <TableCell
        className={cx(styles.nameColumn, {
          [styles.is_idented]: isInTeam && hideNumbers,
        })}
        top
        colSpan={showTeams && !hideNumbers && !isInTeam ? 2 : 1}
      >
        <div className={styles.nameContainer}>
          <div className={styles.playerName}>{formatName(participant)}</div>
          {renderShieldQuickPeek?.(participant.userId)}
        </div>
      </TableCell>
      {showEmails && (
        <TableCell top leftGap className={styles.emailColumn}>
          {participant.email}
        </TableCell>
      )}
      {showGroups && (
        <TableCell className={styles.groupColumn} top leftGap>
          {groupIsRegion ? participant.rcfRegion || groupName : groupName}
        </TableCell>
      )}
      {isRcf && (
        <TableCell className={styles.groupColumn} top leftGap left>
          {participant.rcfRegion || (groupIsRegion ? groupName : null)}
        </TableCell>
      )}
      {isRcf && showEmails && (
        <TableCell centered top leftGap>
          {yearOfBirth}
        </TableCell>
      )}
      {isRcf && showEmails && (
        <TableCell leftGap top right>
          {participant.rcfId}
        </TableCell>
      )}
      {time}
    </TableRow>
  );
}
